export interface CtaNavigationOptions {
  setActivePublicPage?: (page: any, overrideParam?: string | null) => void;
  setSelectedLeaderId?: (leaderId: string) => void;
  leaderId?: string;
  setSelectedJournalSlug?: (slug: string) => void;
  journalSlug?: string;
  openProspectusModal?: () => void;
  openBioModal?: () => void;
  defaultTarget?: string;
}

/**
 * Handles unified CTA and "Read More" navigation across all pages and subpages.
 * Supports internal page routing, modal triggers, and external URLs.
 */
export const handleCtaNavigation = (
  targetPage?: string,
  externalUrl?: string,
  options: CtaNavigationOptions = {}
) => {
  const target = targetPage || options.defaultTarget || 'about';

  // 1. Prospectus modal trigger
  if (target === 'modal:prospectus' || target === 'prospectus-modal') {
    if (options.openProspectusModal) {
      options.openProspectusModal();
    } else if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('oltraya:open-prospectus'));
    }
    return;
  }

  // 2. Leader Bio modal trigger
  if (target === 'modal:bio') {
    if (options.openBioModal) {
      options.openBioModal();
    }
    return;
  }

  // 3. Custom External URL
  if (target === 'external' || target.startsWith('http://') || target.startsWith('https://') || target.startsWith('mailto:') || target.startsWith('tel:')) {
    const url = target === 'external' ? externalUrl : target;
    if (url) {
      if (url.startsWith('mailto:') || url.startsWith('tel:')) {
        window.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
    return;
  }

  // 4. Leadership detail route with specific leader selection
  if (
    target === 'leadership-detail' ||
    target === 'leadership-nitinmudaliar' ||
    target === 'leadership-chandnirav' ||
    target === 'leadership-rickyyeole' ||
    target.startsWith('leadership-') ||
    target.startsWith('leadership/')
  ) {
    let resolvedLeader = options.leaderId;
    if (!resolvedLeader) {
      const lower = target.toLowerCase();
      if (lower.includes('chandni')) resolvedLeader = 'chandni-rav';
      else if (lower.includes('ricky')) resolvedLeader = 'ricky';
      else resolvedLeader = 'nitin';
    }

    if (options.setSelectedLeaderId && resolvedLeader) {
      options.setSelectedLeaderId(resolvedLeader);
    }
    if (options.setActivePublicPage) {
      options.setActivePublicPage('leadership-detail', resolvedLeader);
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }

  // 5. Journal detail route with specific article selection
  if (
    target === 'journal-detail' ||
    target.startsWith('journal/') ||
    (target.startsWith('journal-') && target !== 'journal')
  ) {
    let resolvedSlug = options.journalSlug;
    if (!resolvedSlug) {
      if (target.startsWith('journal/')) {
        resolvedSlug = target.replace(/^journal\//, '');
      } else if (target.startsWith('journal-') && target !== 'journal-detail') {
        resolvedSlug = target.replace(/^journal-/, '');
      } else {
        resolvedSlug = 'the-most-expensive-thing';
      }
    }

    if (options.setSelectedJournalSlug && resolvedSlug) {
      options.setSelectedJournalSlug(resolvedSlug);
    }
    if (options.setActivePublicPage) {
      options.setActivePublicPage('journal-detail', resolvedSlug);
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    return;
  }

  // 6. Standard internal pages
  if (options.setActivePublicPage) {
    options.setActivePublicPage(target);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
};
